import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from 'ui/lib/utils';
import { useAuth } from 'hooks/useAuth';
import { getVersion } from 'services';
import misionesBlanco from 'assets/Misiones-Blanco.svg';
import { getVisibleNavigation, isActivePath, isNavGroup, type NavGroup } from './navigation';
import { useShell } from './ShellProvider';

const VITE_VERSION_FRONT = import.meta.env.VITE_VERSION_FRONT;

const itemClass = (active: boolean, collapsed: boolean) =>
	cn(
		'flex w-full items-center gap-3 border-l-[3px] bg-transparent px-4 py-2 text-left text-sm font-medium no-underline transition-colors',
		'text-[var(--safi-sidebar-text)] hover:bg-[var(--safi-sidebar-hover-bg)]',
		'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white',
		active ? 'border-[var(--safi-sidebar-accent)] bg-[var(--safi-sidebar-active-bg)]' : 'border-transparent',
		collapsed && 'justify-center px-0'
	);

const SidebarVersion = () => {
	const [apiVersion, setApiVersion] = useState('');

	useEffect(() => {
		getVersion()
			.then((res) => setApiVersion((res as { data: string }).data))
			.catch((error: unknown) => console.error(error));
	}, []);

	return (
		<p className="px-4 pb-2 text-xs text-[var(--safi-sidebar-text)] opacity-70">
			Versión {VITE_VERSION_FRONT} | API {apiVersion}
		</p>
	);
};

interface SidebarNavProps {
	collapsed: boolean;
	onNavigate?: () => void;
	onExpandRequest?: () => void;
}

const SidebarNav = ({ collapsed, onNavigate, onExpandRequest }: SidebarNavProps) => {
	const { pathname } = useLocation();
	const { roles } = useAuth();
	const entries = useMemo(() => getVisibleNavigation(roles), [roles]);
	const hasActiveItem = (group: NavGroup) => group.items.some((item) => isActivePath(pathname, item.to));

	const [open, setOpen] = useState<Set<string>>(
		() =>
			new Set(
				entries
					.filter(isNavGroup)
					.filter(hasActiveItem)
					.map((group) => group.key)
			)
	);

	// La ruta actual siempre deja expandido su grupo
	useEffect(() => {
		const active = entries.filter(isNavGroup).filter(hasActiveItem);
		if (active.length === 0) return;
		setOpen((prev) =>
			active.every((group) => prev.has(group.key)) ? prev : new Set([...prev, ...active.map((group) => group.key)])
		);
	}, [pathname, entries]);

	const toggleGroup = (group: NavGroup) => {
		if (collapsed) {
			// En modo de íconos no hay dónde mostrar las opciones: se expande la barra con el grupo abierto
			onExpandRequest?.();
			setOpen((prev) => new Set(prev).add(group.key));
			return;
		}
		setOpen((prev) => {
			const next = new Set(prev);
			if (!next.delete(group.key)) next.add(group.key);
			return next;
		});
	};

	return (
		<nav
			aria-label="Menú principal"
			className="flex-1 overflow-y-auto py-3">
			<ul className="m-0 flex list-none flex-col p-0">
				{entries.map((entry) => {
					const Icon = entry.icon;

					if (!isNavGroup(entry)) {
						const active = isActivePath(pathname, entry.to);
						return (
							<li key={entry.key}>
								<Link
									to={entry.to}
									aria-label={entry.label}
									aria-current={active ? 'page' : undefined}
									title={collapsed ? entry.label : undefined}
									onClick={onNavigate}
									className={itemClass(active, collapsed)}>
									<Icon
										size={20}
										aria-hidden="true"
									/>
									{!collapsed && <span className="flex-1">{entry.label}</span>}
								</Link>
							</li>
						);
					}

					const isOpen = !collapsed && open.has(entry.key);
					return (
						<li key={entry.key}>
							<button
								type="button"
								aria-expanded={isOpen}
								aria-controls={`nav-group-${entry.key}`}
								aria-label={entry.label}
								title={collapsed ? entry.label : undefined}
								onClick={() => toggleGroup(entry)}
								className={cn(itemClass(hasActiveItem(entry), collapsed), 'cursor-pointer')}>
								<Icon
									size={20}
									aria-hidden="true"
								/>
								{!collapsed && (
									<>
										<span className="flex-1">{entry.label}</span>
										<ChevronDown
											size={14}
											aria-hidden="true"
											className={cn('transition-transform duration-200', isOpen && 'rotate-180')}
										/>
									</>
								)}
							</button>
							{isOpen && (
								<ul
									id={`nav-group-${entry.key}`}
									className="m-0 list-none p-0 pb-1">
									{entry.items.map((item) => {
										const active = isActivePath(pathname, item.to);
										return (
											<li key={item.key}>
												<Link
													to={item.to}
													aria-current={active ? 'page' : undefined}
													onClick={onNavigate}
													className={cn(
														'flex items-center py-1.5 pr-4 pl-12 text-[13px] no-underline transition-colors',
														'text-[var(--safi-sidebar-text)] hover:bg-[var(--safi-sidebar-hover-bg)]',
														'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white',
														active && 'bg-[var(--safi-sidebar-active-bg)] font-semibold'
													)}>
													{item.label}
												</Link>
											</li>
										);
									})}
								</ul>
							)}
						</li>
					);
				})}
			</ul>
		</nav>
	);
};

const Brand = ({ collapsed }: { collapsed: boolean }) => (
	<div className="flex h-[52px] shrink-0 items-center justify-center border-b border-[var(--safi-sidebar-border)] px-4">
		{collapsed ? (
			<span
				aria-hidden="true"
				className="text-lg font-semibold tracking-wide text-[var(--safi-sidebar-text-active)]">
				BF
			</span>
		) : (
			<img
				src={misionesBlanco}
				alt="Gobierno de Misiones"
				className="h-8 w-auto"
			/>
		)}
	</div>
);

const FOCUSABLE = 'a[href], button:not([disabled])';

const MobileDrawer = ({ onClose }: { onClose: () => void }) => {
	const panelRef = useRef<HTMLDivElement>(null);

	// Foco dentro del menú al abrir, Escape para cerrar, Tab que no sale del menú y foco devuelto al cerrar
	useEffect(() => {
		const previous = document.activeElement as HTMLElement | null;
		panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') return onClose();
			if (event.key !== 'Tab' || !panelRef.current) return;
			const focusables = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			previous?.focus();
		};
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-50 lg:hidden">
			<div
				aria-hidden="true"
				onClick={onClose}
				className="absolute inset-0 bg-black/50"
			/>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-label="Menú de navegación"
				className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-[var(--safi-sidebar-bg)]">
				<div className="flex items-center pr-2">
					<div className="min-w-0 flex-1">
						<Brand collapsed={false} />
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Cerrar menú"
						className="cursor-pointer rounded-sm p-2 text-[var(--safi-sidebar-text)] hover:bg-[var(--safi-sidebar-hover-bg)] focus-visible:outline-2 focus-visible:outline-white">
						<X
							size={20}
							aria-hidden="true"
						/>
					</button>
				</div>
				<SidebarNav
					collapsed={false}
					onNavigate={onClose}
				/>
				<SidebarVersion />
			</div>
		</div>
	);
};

/** Barra lateral de escritorio y, en pantallas angostas, el menú que se abre desde la cabecera. */
export const Sidebar = () => {
	const { collapsed, setCollapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useShell();
	const { pathname } = useLocation();

	// Elegir una opción (o navegar por cualquier otro medio) cierra el menú móvil
	useEffect(() => setMobileOpen(false), [pathname, setMobileOpen]);

	return (
		<>
			<aside
				aria-label="Barra lateral"
				className={cn(
					'hidden shrink-0 flex-col bg-[var(--safi-sidebar-bg)] transition-[width] duration-200 ease-in-out lg:flex',
					collapsed ? 'w-16' : 'w-64'
				)}>
				<Brand collapsed={collapsed} />
				<SidebarNav
					collapsed={collapsed}
					onExpandRequest={() => setCollapsed(false)}
				/>
				{!collapsed && <SidebarVersion />}
				<button
					type="button"
					onClick={toggleCollapsed}
					aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
					className="flex h-10 cursor-pointer items-center justify-center border-t border-[var(--safi-sidebar-border)] text-[var(--safi-sidebar-text)] transition-colors hover:bg-[var(--safi-sidebar-hover-bg)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white">
					{collapsed ? (
						<ChevronRight
							size={18}
							aria-hidden="true"
						/>
					) : (
						<ChevronLeft
							size={18}
							aria-hidden="true"
						/>
					)}
				</button>
			</aside>
			{mobileOpen && <MobileDrawer onClose={() => setMobileOpen(false)} />}
		</>
	);
};
